import type { PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';
import type {
  MenuItemWithChildren,
  MenuPreviewStateMap,
  ActiveAnchor,
} from '../Menu.types';
import {
  MenuAnimationName,
  type DropdownAnchorType,
  type MenuAnimationNameType,
} from '../constants';
import { defaultTranslations } from '../constants';

const EMPTY_ANCHOR_URL = '#';

export type MenuContextValue = {
  items: Array<MenuItemWithChildren>;
  partToPreviewStateMap?: MenuPreviewStateMap;
  currentUrl: string;
  activeAnchor?: ActiveAnchor;
  translations: {
    dropdownButtonAriaLabel?: string;
    menuNavAriaLabel?: string;
  };
  customClassNames?: Array<string>;
  navAriaLabel: string;
  menuStyleId?: string;
  getAnimationPackage: () => MenuAnimationNameType;
  isHamburgerMenuOpened?: boolean;
  setIsHamburgerMenuOpened?: (isOpen: boolean) => void;
  a11yProps: React.AriaAttributes;
  dropdownAnchor?: DropdownAnchorType;
};

export const MenuContext = createContext<MenuContextValue>({
  items: [],
  currentUrl: EMPTY_ANCHOR_URL,
  translations: defaultTranslations,
  menuStyleId: '',
  customClassNames: [],
  getAnimationPackage: () => MenuAnimationName.None,
  navAriaLabel: '',
  isHamburgerMenuOpened: undefined,
  setIsHamburgerMenuOpened: undefined,
  a11yProps: {},
});

export const useMenuContext = () => useContext(MenuContext);

export const MenuContextProvider: React.FC<
  PropsWithChildren<MenuContextValue>
> = ({
  children,
  items,
  partToPreviewStateMap,
  currentUrl,
  activeAnchor,
  translations,
  menuStyleId,
  customClassNames,
  getAnimationPackage,
  navAriaLabel,
  isHamburgerMenuOpened,
  setIsHamburgerMenuOpened,
  a11yProps,
  dropdownAnchor,
}) => {
  const contextValue = useMemo<MenuContextValue>(
    () => ({
      items,
      partToPreviewStateMap,
      currentUrl,
      activeAnchor,
      translations,
      menuStyleId,
      customClassNames,
      getAnimationPackage,
      navAriaLabel,
      isHamburgerMenuOpened,
      setIsHamburgerMenuOpened,
      a11yProps,
      dropdownAnchor,
    }),
    [
      items,
      partToPreviewStateMap,
      currentUrl,
      activeAnchor,
      translations,
      menuStyleId,
      customClassNames,
      getAnimationPackage,
      navAriaLabel,
      isHamburgerMenuOpened,
      setIsHamburgerMenuOpened,
      a11yProps,
      dropdownAnchor,
    ],
  );

  return (
    <MenuContext.Provider value={contextValue}>{children}</MenuContext.Provider>
  );
};
